import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { TrashIcon } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { del, pdel, post } from "@/api";
import { Models } from "@/api/types";
import { Loader, Text } from "@/atoms";
import { CartAvailablePromotion } from "@/modules/cart";
import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
  useCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { GiftCardMultiPreview } from "@/modules/giftCards";
import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import CheckIcon from "atoms/icon/icons/ic_check_small_ol.svg";
import PromoIcon from "atoms/icon/icons/ic_offers+1_nav_ol.svg";
import PromoIconDot from "atoms/icon/icons/ic_offers_nav_ol.svg";
import { TermsAndCondition } from "atoms/terms-and-condition/TermsAndCondition";
import { TextButton } from "atoms/text-button/TextButton";
import { TextField } from "atoms/text-field/TextField";
import { getProductTitle, isBuyXGetYDiscount } from "services/HelperService";
import { formatCurrency } from "toolbox/Money";

type GiftCardCartContainerProps = {
  platform: Models<"PlatformModel">;
  salesAssociates: Models<"SalesAssociateModel">[];
  showNavbar: boolean;
  membershipDiscount: Models<"GetMembershipDetailsResult"> | null;
  cart: Models<"ShopDisplayOrderResult">;
  initialLineItems: Models<"CartLineItemModel">[];
  listings: Models<"ListingModel">[];
  initialLocations: Models<"GetPlatformLocationsResult">[];
  giftCards: Models<"DisplayGiftCardListing">[];
  transfers: Models<"DisplayGiftCardTransferItem">[];
};

const deleteLineItemFromCart = pdel(
  "/orders/current/lineitems/{shopDisplayOrderLineItemId}"
);

export const GiftCardCartContainer = ({
  cart: initialCart,
  transfers,
  platform,
  giftCards,
}: GiftCardCartContainerProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isDeleting, setIsDeleting] = useState(false);
  const [coupon, setCoupon] = useState<string>("");
  const [rewardId, setRewardId] = useState<string>("");
  const [discountId, setDiscountId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [promoSuccessMessage, setPromoSuccessMessage] = useState<string>("");
  const [promoErrorMessage, setPromoErrorMessage] = useState<string>("");

  const { data: cart, isFetching } = useCurrentOrderDetails({
    initialData: initialCart,
  });

  // Fetch available promos for the current cart
  const { data: availablePromos } = useQuery({
    queryKey: ["/orders/current/available-promos"],
    queryFn: () => get("/orders/current/available-promos"),
  });

  // Handler for applying promo codes
  const handlePromoSubmit = useCallback(
    async (promoCode: string) => {
      setLoading(true);
      try {
        await post("/orders/current/promoCode", {
          // @ts-expect-error body type is marked as `object` in the API, but it actually expects a string
          $data: promoCode,
        });
        setPromoSuccessMessage(
          `Promo code "${promoCode}" applied successfully!`
        );
        setPromoErrorMessage("");
        await queryClient.invalidateQueries({
          queryKey: ["/orders/current/available-promos"],
        });
        await invalidateCurrentOrderDetails();
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to apply promo code";
        setPromoErrorMessage(errorMessage);
        setPromoSuccessMessage("");
      } finally {
        setLoading(false);
      }
    },
    [queryClient]
  );

  // Handler for removing promo codes
  const handlePromoDelete = useCallback(async () => {
    const promoCodeId = cart?.discountSummary?.discounts?.find(
      (d) => d.promoCode
    )?.promoCodeId;

    if (!promoCodeId) return;

    setLoading(true);
    try {
      await del("/orders/current/promoCode/{promoCodeId}", {
        promoCodeId,
      });
      setCoupon("");
      setRewardId("");
      setDiscountId("");
      setPromoSuccessMessage("");
      await queryClient.invalidateQueries({
        queryKey: ["/orders/current/available-promos"],
      });
      await invalidateCurrentOrderDetails();
    } catch (error) {
      console.error({ error });
    } finally {
      setLoading(false);
    }
  }, [cart, queryClient]);

  const promoCode = cart?.discountSummary?.discounts?.find(
    (discount) => discount.promoCode
  );

  React.useEffect(() => {
    if (promoCode) {
      setCoupon(promoCode.promoCode ?? "");
      setRewardId(promoCode.rewardId ?? "");
      setDiscountId(promoCode.discountId ?? "");
    } else {
      setCoupon("");
      setRewardId("");
      setDiscountId("");
    }
  }, [promoCode]);

  const couponAmountOff = useMemo(() => {
    if (!promoCode) return 0;
    return promoCode.amountOff > 0
      ? promoCode.amountOff
      : promoCode.membershipsDiscountTotal ?? 0;
  }, [promoCode]);

  const appliedPromoTitle = useMemo(() => {
    const promoFromCartDetails = cart?.discountSummary?.discounts?.find(
      (d) => d.membershipId == null
    );
    const promoFromAvailablePromos = availablePromos?.find((promo) =>
      cart?.discountSummary?.discounts?.find(
        (d) => d.discountId == promo.discountId
      )
    );

    return (
      promoFromCartDetails?.title ?? promoFromAvailablePromos?.title ?? null
    );
  }, [cart, availablePromos]);

  const handleCheckoutClick = () => {
    navigate("/gift-cards/checkout");
  };

  const cartItems = cart?.shopDisplayOrderLineItems || [];
  const numberOfItems = cartItems.length;

  // Show loading state
  if (isFetching && !cart) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-gray-900">
            Loading cart...
          </div>
        </div>
      </div>
    );
  }

  const renderEmptyCart = () => (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col gap-4 text-center ">
        <div className="text-lg font-medium text-gray-900">
          Your cart is empty
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Add some gift cards to get started
        </div>
        <Button
          onClick={() => {
            navigate("/gift-cards/purchase");
          }}
        >
          Purchase Gift Cards
        </Button>
      </div>
    </div>
  );

  const getSummaryItem = (name: string) =>
    cart?.orderSummaryItems?.find((i) => i.name === name)?.value;

  const subTotal = getSummaryItem("SubTotal");
  const tax = getSummaryItem("Tax");
  const serviceFee =
    getSummaryItem("ServiceFee") || getSummaryItem("CreditCardFee");
  const estimatedTotal = getSummaryItem("EstimatedTotal");

  const taxAndServiceFee = (tax || 0) + (serviceFee || 0);

  const discountSummary = cart?.discountSummary;
  const discounts = discountSummary?.discounts;

  const deleteLineItem = async (shopDisplayOrderLineItemId: string) => {
    try {
      setIsDeleting(true);

      await deleteLineItemFromCart({ shopDisplayOrderLineItemId });

      // Invalidate and refetch cart data
      await invalidateCurrentOrderDetails();
    } catch (error) {
      console.error("Failed to delete cart item:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderOrderInformation = () => (
    <div className="flex w-full flex-col ">
      <div className="border-b border-gray-200 p-4">
        <h1 className="text-xl font-semibold text-gray-900">
          Order summary ({numberOfItems})
        </h1>
      </div>

      <div className="flex-1 space-y-4 p-4">
        {cartItems.map((item) => {
          const itemTitle = getProductTitle(item.productName, item.name);
          const itemDiscount =
            discounts?.find(
              (discount) => discount.shopDisplayOrderLineItemId === item.id
            )?.packagesDiscountTotal ||
            item.discountTotal ||
            0;

          const transfer = transfers?.find(
            (transfer) => transfer.id === item.packageTransferId
          );

          return (
            <div key={item.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      deleteLineItem(item.id);
                    }}
                  >
                    <TrashIcon color="red" />
                  </button>

                  <div>
                    <Text
                      className="font-medium text-gray-900"
                      variant="body1"
                      text={itemTitle}
                      component="p"
                    />

                    <Text
                      className="font-medium text-gray-900"
                      variant="body3"
                      text={transfer?.recipientInformation}
                      component="p"
                    />
                  </div>
                </div>

                <span className="font-medium text-gray-900">
                  {formatCurrency(item.cost)}
                </span>
              </div>
              {itemDiscount > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-red-600 text-sm">Discount</span>
                  <span className="text-red-600 text-sm font-medium">
                    -{formatCurrency(itemDiscount)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-3 border-t border-gray-200 p-4">
        <div className="flex justify-between text-gray-700">
          <span>Subtotal</span>
          <span>{formatCurrency(subTotal ?? 0)}</span>
        </div>

        <div className="flex items-center justify-between text-gray-700">
          <div className="flex items-center gap-1">
            <span>Tax & service fee</span>
          </div>
          <span>{formatCurrency(taxAndServiceFee)}</span>
        </div>

        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between text-lg font-semibold text-gray-900">
            <span>Order total</span>
            <span>{formatCurrency(estimatedTotal ?? 0)}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 p-4">
        <ApplyPromoCodeAccordion
          coupon={coupon}
          rewardId={rewardId}
          discountId={discountId}
          availablePromos={availablePromos?.filter(
            (p) =>
              p.totalDiscount > 0 &&
              !p.rewardId &&
              !isBuyXGetYDiscount(p.discount)
          )}
          onPromoSubmit={handlePromoSubmit}
          onDelete={handlePromoDelete}
          successMsg={promoSuccessMessage}
          errorMsg={promoErrorMessage}
          loading={loading}
          appliedPromoTitle={appliedPromoTitle}
          couponAmountOff={couponAmountOff}
          cartPreFeeTotal={cart?.preFeeTotal ?? 0}
        />
      </div>

      <div className="border-t border-gray-200 p-4">
        <TermsAndCondition hideText={true} />
      </div>

      <div className="mt-6  border-gray-200 p-4">
        <Button
          onClick={handleCheckoutClick}
          fullWidth
          style="primary"
          size="large"
        >
          Checkout now
        </Button>
      </div>
    </div>
  );

  const cards = cartItems.map((item) => {
    const transfer = transfers?.find(
      (transfer) => transfer.id === item.packageTransferId
    );

    const giftCardListing = giftCards?.find((gc) =>
      gc.giftCards.some((gci) => gci.packageId === item.shopDisplayPackageId)
    );
    const imageUrl = giftCardListing?.imageUri ?? item.imageUrl ?? "";

    return {
      imageUrl: imageUrl,
      cost: item.cost ?? 0,
      name: item.name ?? "",
      recipientInformation: transfer?.recipientInformation ?? "",
      senderInformation: transfer?.senderInformation ?? "",
      logoImageUrl: platform.logoImageUrl ?? "",
      message: transfer?.personalMessage ?? "",
    };
  });

  return (
    <div className="GiftCardsPurchaseContainer relative flex h-auto min-h-[calc(100vh-64px)] flex-col overflow-hidden lg:h-[calc(100vh-64px)] lg:flex-row">
      <motion.div
        className="top-0 hidden items-center justify-center bg-brand-color/5 lg:sticky lg:flex lg:h-screen lg:w-1/2 lg:flex-1"
        initial={{ opacity: 0, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: 0.4 }}
      >
        <GiftCardMultiPreview cards={cards} />
      </motion.div>
      <motion.div
        className="flex flex-1 justify-center bg-gray-50 p-6 lg:h-full lg:overflow-y-auto"
        initial={{ opacity: 0, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: 0.2 }}
      >
        <div className="flex w-full flex-col justify-center py-4 sm:max-w-xl">
          {!cart || cartItems.length === 0
            ? renderEmptyCart()
            : renderOrderInformation()}
        </div>
      </motion.div>

      {isDeleting && <Loader fullScreen visible={isDeleting} />}
    </div>
  );
};

type ApplyPromoCodeAccordionProps = {
  coupon: string;
  rewardId: string;
  discountId: string;
  availablePromos?: Models<"RewardPromo">[] | null;
  onPromoSubmit: (promo: string) => void;
  onDelete: () => void;
  successMsg: string;
  errorMsg: string;
  loading: boolean;
  appliedPromoTitle: string | null;
  couponAmountOff: number;
  cartPreFeeTotal: number;
};

const ApplyPromoCodeAccordion = ({
  coupon,
  rewardId,
  discountId,
  availablePromos,
  onPromoSubmit,
  onDelete,
  successMsg,
  errorMsg,
  loading,
  appliedPromoTitle,
  couponAmountOff,
  cartPreFeeTotal,
}: ApplyPromoCodeAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState("");

  const hasAppliedPromo = Boolean(coupon || rewardId);
  const hasAvailablePromos = (availablePromos?.length ?? 0) > 0;

  const getIcon = () => {
    if (hasAppliedPromo) return CheckIcon;
    if (hasAvailablePromos) return PromoIconDot;
    return PromoIcon;
  };

  const getStatusText = () => {
    if (hasAppliedPromo) {
      return appliedPromoTitle || "Promo code applied";
    }
    if (hasAvailablePromos) {
      return `You have ${availablePromos?.length} promotion${
        (availablePromos?.length ?? 0) > 1 ? "s" : ""
      } available`;
    }
    return "Apply a promo code";
  };

  const handleApplyPromo = () => {
    if (promoCodeInput.trim()) {
      onPromoSubmit(promoCodeInput.trim());
      setPromoCodeInput("");
    }
  };

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
    setPromoCodeInput("");
  };

  const couponIsFromAvailablePromotionsList = availablePromos?.some(
    (p) =>
      (coupon == p.promoCode && discountId == p.discountId) ||
      discountId == p.discountId
  );

  return (
    <div
      className={`w-full rounded border border-light-grey ${
        loading ? "pointer-events-none opacity-60" : ""
      }`}
    >
      <div className="flex h-16 w-full items-center justify-between py-3 pl-3 pr-3">
        <div className="flex items-center gap-2">
          <Icon
            svg={getIcon()}
            color={hasAppliedPromo ? "successGreen" : "secondaryText"}
            size="small"
          />
          <div className="flex flex-col">
            <div className="line-clamp-1 text-bold2">
              {loading ? "Loading..." : getStatusText()}
            </div>
            {!hasAppliedPromo && !loading && (
              <div className="text-body2 text-secondary">
                {hasAvailablePromos
                  ? "Tap to view all available promotions"
                  : "Tap to apply your code"}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TextButton onClick={handleToggleExpanded} xsText disabled={loading}>
            {isExpanded ? "Close" : "Open"}
          </TextButton>
        </div>
      </div>

      {isExpanded && (
        <div className="w-full space-y-3 border-t border-light-grey px-3 pb-3 pt-3">
          {/* Show available promotions */}
          {availablePromos &&
            availablePromos.map((promo, i) => {
              const isFreeServicePromo =
                promo.discount?.percentOff === 1 &&
                !isBuyXGetYDiscount(promo.discount) &&
                promo.discount.inclusions?.some(
                  (inclusion) => inclusion.membershipId !== null
                );

              const isThisPromoApplied =
                coupon == promo.promoCode ||
                rewardId == promo.rewardId ||
                discountId == promo.discountId;

              return (
                <div key={`${promo.rewardId ?? promo.promotionId}_${i}`}>
                  <CartAvailablePromotion
                    cartPreFeeTotal={cartPreFeeTotal}
                    textButton={
                      loading ? "..." : isThisPromoApplied ? "REMOVE" : "APPLY"
                    }
                    onClick={() => {
                      if (loading) return;
                      if (isThisPromoApplied) {
                        onDelete();
                      } else {
                        if (promo?.promoCode) {
                          onPromoSubmit(promo.promoCode);
                        }
                      }
                    }}
                    title={isFreeServicePromo ? "Free service" : promo.title}
                    totalDiscount={promo.totalDiscount}
                    expiresOn={promo.expiresOn}
                    isHighlighted={isThisPromoApplied}
                    isReward={promo.rewardId ? true : false}
                    appliesToMembership={!!promo.appliesToMembership}
                    minimumCartSpendAmount={promo.minimumCartSpendAmount}
                    requiresMinimumCartSpend={
                      promo.hasMinimumCartSpendRequirement
                    }
                    promotion={promo as Models<"RewardPromo">}
                  />
                </div>
              );
            })}

          {/* Show applied promo if it's not from available list */}
          {coupon && !couponIsFromAvailablePromotionsList && (
            <CartAvailablePromotion
              cartPreFeeTotal={cartPreFeeTotal}
              textButton={loading ? "..." : "REMOVE"}
              onClick={() => {
                if (loading) return;
                onDelete();
              }}
              title={appliedPromoTitle || successMsg || coupon}
              totalDiscount={couponAmountOff}
              isHighlighted={true}
            />
          )}

          {/* Manual promo code entry */}
          {(!coupon || (coupon && couponIsFromAvailablePromotionsList)) && (
            <div className="mt-3">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <TextField
                    label="Promo code"
                    placeholder="Enter promo code"
                    value={promoCodeInput}
                    invalid={!!errorMsg}
                    onChange={(e) => {
                      setPromoCodeInput(String(e));
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") {
                        handleApplyPromo();
                      }
                    }}
                  />
                  {errorMsg && !loading && (
                    <p className="text-red-600 mt-1 text-xs">{errorMsg}</p>
                  )}
                </div>
                <TextButton
                  onClick={handleApplyPromo}
                  xsText
                  disabled={!promoCodeInput.trim() || loading}
                >
                  {loading ? "..." : "APPLY"}
                </TextButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
